import { useUserSettings } from "@services/UserSettings";
import type { IHealthMonitorFeature, IHealthMonitorFeatureDto } from "./IHealthMonitorFeature";
import { isNotProduction } from "@modules/Utilities/Environments";
import { DeviceInstallationStatus } from "@domain/General/DeviceInstallationStatus/DeviceInstallationStatus";
import dayjs, { Dayjs } from "dayjs";
import { BatteryHealth } from "./BatteryHealth";
import type { IBatteryHealth } from "./IBatteryHealth";
import type { IScanEvent } from "./IScanEvent";
import { AutoInstalledState } from "@domain/General/Enums/AutoInstalledState";

export class HealthMonitorFeature implements IHealthMonitorFeature
{

    title: string;
    type: number;
    status: DeviceInstallationStatus;
    isPaired: boolean;
    utcDateLastPaired?: Dayjs;
    utcDateLastScan?: Dayjs;
    batteryHealth: IBatteryHealth;
    scanEvents: IScanEvent[]
    hasIssueDetected: boolean;
    isEnabledInAppSettings: boolean;
    isInstalled: boolean;

    constructor( model: IHealthMonitorFeatureDto )
    {

        // if( isNotProduction() )
        // {
        //     this.createMockData( model );
        // }

        const settings = useUserSettings();
        this.title = model.title;
        this.type = model.type;
        this.status = model.status;
        this.isPaired = model.isPaired;
        this.isEnabledInAppSettings = model.isEnabledInAppSettings;
        this.utcDateLastPaired = model.utcDateLastPaired != null ? dayjs( model.utcDateLastPaired ) : undefined;
        this.utcDateLastScan = model.utcDateLastScan != null ? dayjs( model.utcDateLastScan ) : undefined;
        this.batteryHealth = new BatteryHealth( model.batteryHealth );
        this.hasIssueDetected = settings.hasHealthMonitorWarning || model.hasIssueDetected;
        this.scanEvents = [];

        // The health monitor was not previously paired, and now it is paired
        // If the health monitor was set to disabled, set it back to auto  
        if( settings.wasHealthMonitorPaired == false && ( this.utcDateLastPaired != null || this.utcDateLastScan != null ) )
        {
            settings.wasHealthMonitorPaired = true;
            if( settings.healthMonitorEnabledState == AutoInstalledState.NotInstalled )
            {
                settings.healthMonitorEnabledState = AutoInstalledState.Auto;
            }
        }

        this.isInstalled = ( isNotProduction() && settings.healthMonitorEnabledState == AutoInstalledState.Installed ) 
            || ( settings.healthMonitorEnabledState == AutoInstalledState.Auto && model.status == DeviceInstallationStatus.Installed );

    }


    /**
     * True if the feature is enabled
     */
    get isEnabled()
    {
        return this.isInstalled || this.isEnabledInAppSettings;
    }
    
    /**
     * True if there has been a scan and the date of that scan is within the last 30 days
     */
    get hasRecentTelemetryData()
    {
        return this.utcDateLastScan != undefined && this.utcDateLastScan.isBefore( dayjs().subtract( 30, 'days' ) );
    }
    
    /**
     * True if the health monitor is not paired or if the date of the last scan is old.
     */
    get showHealthMonitorDisconnectedWorflow()
    {
        return this.isInstalled 
            && ( this.isPaired == false || ( this.hasRecentTelemetryData == false && this.utcDateLastScan != undefined ) );
    }

    /**
     * Updates the installed state
     */
    updateInstalledState()
    {
        const settings = useUserSettings();
        this.isInstalled = ( isNotProduction() && settings.healthMonitorEnabledState == AutoInstalledState.Installed ) 
            || ( settings.healthMonitorEnabledState == AutoInstalledState.Auto && this.status == DeviceInstallationStatus.Installed );
    }

    /**
     * Creates mock data for the health monitor
     * TODO: Remove this once the backend has been implemented
     * @param model
     */
    createMockData( model: IHealthMonitorFeatureDto )
    {

        const settings = useUserSettings();

        if( settings.healthMonitorEnabledState == AutoInstalledState.Installed )
        {
            model.status = DeviceInstallationStatus.Installed;
        }

        model.isPaired = true;

        if( model.batteryHealth == null || model.batteryHealth.batteryVoltageReadings == null || model.batteryHealth.batteryVoltageReadings.length == 0 )
        {
            model.batteryHealth = {
                batteryVoltageReadings: [ 11.8, 11.9, 12.2, 12.1, 11.9, 12.0, 12.4, 12.3, 12.5, 12.6 ]
                    .map(  v => ({ voltage: v, readingTimestamp: '' }) ) // Chronological order, oldest first
            };
        }

    }

    /**
     * Toggles the insertion/removal of an error scan into the health monitor history
     * @returns
     */
    toggleMockErrorCode( showError: boolean )
    {
        this.hasIssueDetected = true;
    }

}
